import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Car, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function CategorySettings() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", image: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/vehicle-categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openDialog = (cat = null) => {
    setEditing(cat);
    setForm(cat ? { name: cat.name, image: cat.image || "" } : { name: "", image: "" });
    setOpen(true);
  };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Saiz imej melebihi 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/vehicle-categories/${editing.id}`, form);
        toast.success("Kategori dikemas kini");
      } else {
        await api.post("/vehicle-categories", form);
        toast.success("Kategori ditambah");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Padam kategori "${cat.name}"?`)) return;
    try {
      await api.delete(`/vehicle-categories/${cat.id}`);
      toast.success("Kategori dipadam");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const move = async (index, dir) => {
    const next = [...cats];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setCats(next);
    try {
      await api.put("/vehicle-categories/reorder", { ids: next.map((c) => c.id) });
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="category-settings">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold">Kategori Kenderaan</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Urus kategori, imej dan susunan — dipaparkan pada senarai Kenderaan, penapis & borang pendaftaran.
            </CardDescription>
          </div>
          <Button size="sm" className="bg-[#10056E] hover:bg-[#2a17a8]" onClick={() => openDialog()} data-testid="add-category-btn">
            <Plus className="h-4 w-4 mr-2" /> Tambah Kategori
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {cats.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Tiada kategori.</p>}
        {cats.map((c, i) => (
          <div key={c.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2.5 bg-white" data-testid={`category-row-${c.name}`}>
            <div className="flex flex-col shrink-0">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`category-up-${c.name}`}
                className="h-6 w-6 flex items-center justify-center rounded hover:bg-slate-100 disabled:opacity-25">
                <ChevronUp className="h-4 w-4" />
              </button>
              <button type="button" disabled={i === cats.length - 1} onClick={() => move(i, 1)} data-testid={`category-down-${c.name}`}
                className="h-6 w-6 flex items-center justify-center rounded hover:bg-slate-100 disabled:opacity-25">
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            <div className="h-12 w-20 shrink-0 border rounded-md bg-slate-50 flex items-center justify-center overflow-hidden">
              {c.image ? (
                <img src={c.image} alt={c.name} className="h-full w-full object-contain" />
              ) : (
                <Car className="h-6 w-6 text-slate-300" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold truncate">{c.name}</p>
              <p className="text-xs text-muted-foreground">{c.vehicle_count} kenderaan</p>
            </div>
            <Badge variant="secondary" className="hidden sm:inline-flex text-[10px]">#{i + 1}</Badge>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(c)} data-testid={`category-edit-${c.name}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-[#DC3545]" onClick={() => remove(c)} data-testid={`category-delete-${c.name}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Kemas Kini Kategori" : "Tambah Kategori"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nama Kategori</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Cth: Pikap" data-testid="category-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Imej Kategori</Label>
              <div className="flex items-center gap-3">
                <div className="h-20 w-32 shrink-0 border rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden">
                  {form.image ? (
                    <img src={form.image} alt="Pratonton" className="h-full w-full object-contain" data-testid="category-image-preview" />
                  ) : (
                    <Car className="h-8 w-8 text-slate-300" />
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#10056E] border border-[#10056E]/40 rounded-lg px-3 py-2 cursor-pointer hover:bg-indigo-50">
                    <Upload className="h-4 w-4" /> Muat Naik Imej
                    <input type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="category-image-input" />
                  </label>
                  {form.image && (
                    <button type="button" className="block text-xs text-[#DC3545] hover:underline"
                      onClick={() => setForm((f) => ({ ...f, image: "" }))} data-testid="category-image-remove">
                      Buang imej
                    </button>
                  )}
                  <p className="text-[11px] text-muted-foreground">PNG/JPG, maksimum 1MB.</p>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#10056E] hover:bg-[#2a17a8]" data-testid="category-save-btn">
              {editing ? "Kemas Kini" : "Tambah"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
